import { cn } from "lib/utils";

const noop = () => {};

/**
 * Drop-in replacement for @material-ui/core's <Radio>.
 *
 * This is a styled native <input type="radio">, NOT the Radix-based
 * components/ui/radio-group — see the note at the bottom of this comment.
 *
 * Native is the faithful choice here, not a compromise. MUI's <Radio> used
 * outside a <RadioGroup> was itself a bare input, which is why the call sites
 * drive `checked` from parent state and why PropertyTableCheckbox can give three
 * radios in one logical row three different `name`s. Keeping a real input
 * preserves that behaviour exactly, including the browser-level grouping by
 * `name` (a pre-existing quirk: PropertyTableCheckbox reuses the same three
 * names across every mapped row, so those radios group across rows at the DOM
 * level and rely on React re-asserting `checked` from props — unchanged from
 * MUI, but worth knowing about if a stale selection ever shows up there).
 *
 * Event parity comes for free: a native input fires `onClick` on every click and
 * `onChange` only on an actual change, which is precisely MUI's behaviour and
 * the reason nine of the twelve call sites wire both to the same handler. Sites
 * passing only `onClick` (AdjustableDim, MultiLumSettings) work unmodified, and
 * handlers keep reading `e.target.name` / `e.target.id` / `e.target.value` with
 * no synthetic event in the way.
 *
 * `className`/`style` land on the padded wrapper because MUI applied them to its
 * 42x42 root, so `style={{padding: 0}}` at a call site still collapses the
 * padding the way it used to. Appearance lives in the "Legacy control theming"
 * block in src/index.css, keyed on `.t4t-radio`.
 *
 * ---------------------------------------------------------------------------
 * WHY NATIVE AND NOT shadcn/Radix
 * ---------------------------------------------------------------------------
 * See the fuller note in StandaloneCheckbox.jsx. Short version: the original
 * reason (Radix could not bundle under CRA 5) no longer applies after the Vite
 * migration, but native is still the right call here — MUI's <Radio> outside a
 * <RadioGroup> was itself a bare input, so native preserves the existing
 * grouping and event semantics exactly and needs no adapter. Reconsider during
 * the design-token pass, not as a standalone change.
 * ---------------------------------------------------------------------------
 */
export default function StandaloneRadio({
    checked,
    onChange,
    onClick,
    name,
    value,
    disabled,
    id,
    className,
    style,
}) {
    return (
        <span
            className={cn(
                "inline-flex items-center justify-center rounded-full p-[9px] align-middle",
                className
            )}
            style={style}
        >
            <input
                type="radio"
                className="t4t-radio"
                checked={!!checked}
                disabled={disabled}
                name={name}
                value={value}
                id={id}
                onChange={onChange || noop}
                onClick={onClick}
            />
        </span>
    );
}
